<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Copy, Check, Code2 } from 'lucide-vue-next'
import { ref, watch } from 'vue'

const props = defineProps<{
  open: boolean
  title: string
  description: string
  showCode: boolean
  copied: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'update:showCode', value: boolean): void
  (e: 'copy'): void
  (e: 'save'): void
}>()

const handleCopy = () => {
  emit('copy')
}

const isEditingMode = ref(false)

watch(() => props.open, (val) => {
  if (val) {
    isEditingMode.value = !!localStorage.getItem('editingDiagramCode')
  } else {
    isEditingMode.value = false
  }
}, { immediate: true })
</script>

<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="!max-w-[100vw] !w-screen !h-screen !m-0 !p-0 !rounded-none !border-none flex flex-col bg-background">
      <DialogHeader class="shrink-0 p-4 pb-2 border-b bg-muted/20">
        <div class="flex items-center justify-between">
          <div>
            <DialogTitle class="text-xl flex items-center gap-2">
              <slot name="icon"></slot>
              {{ title }}
            </DialogTitle>
            <DialogDescription class="mt-1">
              {{ description }}
            </DialogDescription>
          </div>
          <div class="flex items-center gap-4">
            <!-- Toolbar Global -->
            <div class="flex flex-wrap gap-2">
              <slot name="toolbar"></slot>
            </div>
            
            <div class="h-6 w-px bg-border mx-2"></div>
            
            <Button 
              variant="outline" 
              size="sm" 
              @click="$emit('update:showCode', !showCode)" 
              :class="showCode ? 'bg-primary/10' : ''"
            >
              <Code2 class="w-4 h-4 mr-2" />
              {{ showCode ? 'Ocultar Panel de Código' : 'Mostrar Panel de Código' }}
            </Button>
            
            <Button variant="outline" size="sm" @click="$emit('update:open', false)">Cancelar</Button>
            <Button size="sm" @click="$emit('save')">
              {{ isEditingMode ? 'Actualizar Diagrama' : 'Insertar en Documento' }}
            </Button>
          </div>
        </div>
      </DialogHeader>
      
      <!-- Main Content (Canvas + Code + Preview) -->
      <div class="flex-1 flex overflow-hidden relative min-h-0">
        <!-- Visual Canvas (Left) -->
        <div class="flex-1 relative bg-slate-100/50 dark:bg-background">
          <div class="absolute inset-0">
            <slot name="canvas"></slot>
          </div>
        </div>

        <!-- Right Sidebar (Code + Preview) -->
        <div v-show="showCode" class="w-[500px] shrink-0 border-l bg-muted flex flex-col overflow-hidden">
          
          <!-- Code Editor (Top half) -->
          <div class="flex-1 flex flex-col border-b bg-background overflow-hidden relative">
            <slot name="code-panel"></slot>
          </div>
          
          <!-- Live Preview (Bottom half) -->
          <div class="flex-1 flex flex-col bg-background overflow-hidden relative">
            <slot name="live-preview"></slot>
          </div>
          
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
